import React from "react";
import { gql } from "@apollo/client";
import { FormattedDate } from "react-intl";

import {
  styled,
  Box,
  Tooltip,
  Fab,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import {
  TruckDelivery as ShipmentIcon,
  Email as EmailIcon,
  MessageBulleted as CommentIcon,
  PhoneMessage as PhoneCallIcon,
  OpenInNew as LinkIcon,
  Receipt as InvoiceIcon,
  CurrencyUsdOff as CreditNoteIcon,
  Replay as ReturnIcon,
} from "mdi-material-ui";

import WebSiteLink from "components/common/WebSiteLink";
import CreateOrderInvoice from "components/billing/CreateOrderInvoice";
import CreateCreditNote from "components/billing/CreateCreditNote";

import OrderActionShipment from "./Shipment";
import OrderActionMail from "./Mail";
import OrderActionComment from "./Comment";
import OrderActionPhoneCall from "./PhoneCall";
import OrderActionFile from "./File";

const ActionButton = styled(Fab)`
  z-index: initial;
`;

ActionButton.defaultProps = {
  color: "primary",
  size: "small",
};

export default function OrderActions({ order }) {
  const [selectedAction, setSelectedAction] = React.useState();

  const userLocale = order.user?.profile?.locale ?? "en";

  return (
    <Box ml={7}>
      <Stack spacing={2} direction="row" mt={1} mb={2}>
        <Tooltip title="Éxpédition">
          <ActionButton onClick={() => setSelectedAction("shipment")}>
            <ShipmentIcon />
          </ActionButton>
        </Tooltip>

        <Tooltip title="Envoyer un mail">
          <ActionButton onClick={() => setSelectedAction("mail")}>
            <EmailIcon />
          </ActionButton>
        </Tooltip>

        <Tooltip title="Ajouter un commentaire">
          <ActionButton onClick={() => setSelectedAction("comment")}>
            <CommentIcon />
          </ActionButton>
        </Tooltip>

        <Tooltip title="Ajouter une convarsation téléphonique">
          <ActionButton onClick={() => setSelectedAction("phoneCall")}>
            <PhoneCallIcon color="inherit" />
          </ActionButton>
        </Tooltip>

        <CreateOrderInvoice order={order}>
          {({ open }) =>
            !order.paymentId && (
              <Tooltip title="Créer une facture pour cette commande">
                <ActionButton onClick={open}>
                  <InvoiceIcon />
                </ActionButton>
              </Tooltip>
            )
          }
        </CreateOrderInvoice>

        <CreateCreditNote invoice={order.invoice}>
          {({ open }) =>
            order.invoice && (
              <Tooltip title={"Créer un avoir (remboursement)"}>
                <div>
                  <ActionButton onClick={open}>
                    <CreditNoteIcon />
                  </ActionButton>
                </div>
              </Tooltip>
            )
          }
        </CreateCreditNote>

        {!order.paymentId && (
          <Tooltip title="Ouvrir la page de paiement">
            <ActionButton
              component={WebSiteLink}
              locale={userLocale}
              to={`/shop/orders/${order.id}/checkout`}
            >
              <LinkIcon />
            </ActionButton>
          </Tooltip>
        )}

        {!!order.paymentId && (
          <Tooltip
            title={
              <>
                <div>Ouvrir la page d’échanges & remboursements</div>
                <div>
                  Valable jusqu'au{" "}
                  <FormattedDate
                    value={order.returnsLimitDate}
                    year="2-digit"
                    month="2-digit"
                    day="2-digit"
                    hour="2-digit"
                    minute="2-digit"
                  />
                </div>
              </>
            }
          >
            <ActionButton
              component={WebSiteLink}
              locale={userLocale}
              to={`/user/orders/${order.id}/returns`}
            >
              <ReturnIcon />
            </ActionButton>
          </Tooltip>
        )}
      </Stack>

      <OrderActionFile order={order} />

      {selectedAction && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            {selectedAction === "comment" && (
              <OrderActionComment
                order={order}
                onSubmitted={() => setSelectedAction(null)}
              />
            )}

            {selectedAction === "phoneCall" && (
              <OrderActionPhoneCall
                order={order}
                onSubmitted={() => setSelectedAction(null)}
              />
            )}

            {selectedAction === "mail" && (
              <OrderActionMail
                order={order}
                onSubmitted={() => setSelectedAction(null)}
              />
            )}

            {selectedAction === "shipment" && (
              <OrderActionShipment
                order={order}
                onSubmitted={() => setSelectedAction(null)}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

OrderActions.fragments = {
  order: gql`
    fragment OrderActions_order on Order {
      id
      paymentId
      returnsLimitDate

      invoice {
        id

        creditNotes {
          id
        }

        ...Billing_CreateCreditNote_invoice
      }

      user {
        profile {
          locale
        }
      }

      ...Billing_CreateOrderInvoice_order

      ...OrderActions_Shipment_order
      ...OrderActions_Mail_order
      ...OrderActions_Comment_order
      ...OrderActions_PhoneCall_order
      ...OrderActions_File_order
    }

    ${CreateOrderInvoice.fragments.order}
    ${CreateCreditNote.fragments.invoice}

    ${OrderActionShipment.fragments.order}
    ${OrderActionMail.fragments.order}
    ${OrderActionComment.fragments.order}
    ${OrderActionPhoneCall.fragments.order}
    ${OrderActionFile.fragments.order}
  `,
};
